import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Lock, ShieldCheck } from "lucide-react";
import { Card, Pill, Spinner, useToast } from "@/components/earnos/kit";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/lib/theme";

const ROLE_COLOR = { super_admin: colors.brandTertiary, admin: colors.brandSecondary, support: colors.info, user: colors.onSurfaceSecondary };

function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" disabled={disabled} data-testid="ai-kill-switch" onClick={() => !disabled && onChange(!checked)} className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${checked ? "bg-danger" : "bg-surface-3"} ${disabled ? "opacity-50" : ""}`}>
      <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isOwner = user?.role === "super_admin";
  const isStaff = isOwner || user?.role === "admin";

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [ai, setAi] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [killBusy, setKillBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const reqs = [apiFetch("/admin/stats"), apiFetch("/admin/users")];
      if (isOwner) { reqs.push(apiFetch("/admin/ai/overview")); reqs.push(apiFetch("/admin/audit")); }
      const res = await Promise.all(reqs);
      setStats(res[0]); setUsers(res[1].users || []);
      if (isOwner) { setAi(res[2]); setAudit(res[3].logs || []); }
    } catch (e) {
      if (e?.status !== 401) toast.show(e.message || "Could not load admin data", "error");
    } finally { setLoading(false); }
  }, [isOwner, toast]);

  useEffect(() => { if (isStaff) load(); else setLoading(false); }, [isStaff, load]);

  const toggleKill = async (enabled) => {
    setKillBusy(true);
    try { await apiFetch("/admin/ai/kill", { method: "POST", body: { enabled } }); toast.show(enabled ? "AI globally paused" : "AI resumed", enabled ? "info" : "success"); await load(); }
    catch (e) { toast.show(e.message || "Failed", "error"); }
    finally { setKillBusy(false); }
  };

  const setRole = async (uid, role) => {
    try { await apiFetch(`/admin/users/${uid}/role`, { method: "POST", body: { role } }); toast.show(`Role updated to ${role}`, "success"); await load(); }
    catch (e) { toast.show(e.message || "Failed", "error"); }
  };

  if (!isStaff) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Lock size={40} className="text-ink-2" />
        <div role="status" className="text-center"><h1 className="text-xl font-semibold text-ink">Administrator access required</h1><p className="text-lg text-ink-2">This area is restricted to administrators.</p></div>
      </div>
    );
  }

  if (loading) return <Spinner className="py-24" />;

  const statItems = [
    { label: "Users", value: stats?.total_users }, { label: "Onboarded", value: stats?.onboarded },
    { label: "Engines", value: stats?.engines }, { label: "Moves done", value: stats?.moves_completed },
    { label: "Offers", value: stats?.offers }, { label: "Leads", value: stats?.leads },
    { label: "AI requests", value: stats?.ai_requests }, { label: "AI cost", value: `$${(stats?.ai_est_cost_usd || 0).toFixed(2)}` },
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} data-testid="admin-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">{isOwner ? "Owner Console" : "Admin Console"}</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statItems.map((s) => (
          <div key={s.label} className="rounded-xl border border-line bg-surface-2 p-4">
            <p className="text-2xl font-extrabold text-ink">{s.value ?? 0}</p>
            <p className="text-sm text-ink-2 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {isOwner ? (
        <Card className="mt-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <p className="text-lg font-extrabold text-ink">Emergency AI shutdown</p>
              <p className="text-sm text-ink-2 mt-0.5">Instantly stops all AI generation across every user.</p>
            </div>
            <Toggle checked={!!ai?.kill_switch} onChange={toggleKill} disabled={killBusy} />
          </div>
          <div className="flex justify-between mt-4 text-sm text-ink-2">
            <span>This month AI cost: <span className="text-ink font-bold">${(ai?.month_est_cost_usd || 0).toFixed(2)}</span></span>
            <span>All-time: ${(ai?.total_est_cost_usd || 0).toFixed(2)}</span>
          </div>
        </Card>
      ) : null}

      <h3 className="text-lg font-extrabold text-ink mt-8 mb-3">Users</h3>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.user_id} className="flex items-start gap-3 rounded-xl border border-line bg-surface-2 p-4">
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-ink">{u.first_name} <span className="text-sm text-ink-2 font-semibold">· {(u.plan || "free").toUpperCase()}</span></p>
              <p className="text-sm text-ink-2">{u.email}</p>
              {isOwner && u.role !== "super_admin" ? (
                <div className="flex gap-2 mt-3">
                  {["user", "support", "admin"].map((r) => (
                    <button key={r} onClick={() => setRole(u.user_id, r)} data-testid={`role-${u.user_id}-${r}`} className={`px-3 py-1 rounded-full text-sm font-bold capitalize border ${u.role === r ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{r}</button>
                  ))}
                </div>
              ) : null}
            </div>
            <Pill label={(u.role || "user").replace("super_admin", "owner").toUpperCase()} color={ROLE_COLOR[u.role] || colors.onSurfaceSecondary} />
          </div>
        ))}
      </div>

      {isOwner ? (
        <>
          <h3 className="text-lg font-extrabold text-ink mt-8 mb-3">Audit log</h3>
          {audit.length === 0 ? <p className="text-sm text-ink-2">No audit entries yet.</p> : audit.slice(0, 40).map((a) => (
            <div key={a.audit_id} className="flex items-start gap-2 py-2 border-b border-line">
              <ShieldCheck size={16} className="text-brand-light mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-ink">{a.action}{a.target ? ` · ${a.target}` : ""}</p>
                <p className="text-sm text-ink-2">{a.actor_role} · {a.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}
